import React, { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Section,
  SelectionGroup,
  SelectionGroupValue,
  SelectionGroupOption,
  LinkWithGAClickButton,
  Typography,
} from 'common/components';
import { Landing } from 'modules/landings-info/landings-info-types';
import { ServiceCategory } from 'modules/service-categories/service-categories-types';
import { analyticsUtils } from 'utils/analytics-utils';
import { getFinalServicesData } from 'landings/services-main/services-main-utils';
import { ServiceTabName, ServiceGroupStructure } from './services-catalog-section-types';
import { getServiceTabList, SERVICES_CONTENT_DATA } from './services-catalog-section-constants';
import {
  catalogTabs,
  catalogGroupIcon,
  catalogItem,
  catalogItemName,
} from './services-catalog-section-styles';
import { ServiceAccordion, ServicePanel } from './components';

type ServicesCatalogSectionProps = {
  landingsList: Landing[] | null;
  serviceCategoriesList: ServiceCategory[] | null;
};

export const ServicesCatalogSection = ({
  landingsList,
  serviceCategoriesList,
}: ServicesCatalogSectionProps) => {
  const { t } = useTranslation();
  const [activeServiceTab, setActiveServiceTab] = useState(ServiceTabName.All);
  const serviceTabList = getServiceTabList(t);

  // Проверяем, является ли пустым контент всего таба
  const isNullTabContent = useCallback(
    (serviceTab: ServiceTabName) =>
      SERVICES_CONTENT_DATA[serviceTab].every(
        serviceData =>
          getFinalServicesData(serviceData.list, landingsList, serviceCategoriesList).length === 0
      ),
    [landingsList, serviceCategoriesList]
  );

  // Список активных табов (где контент непустой)
  const actualTabs = useMemo(
    () =>
      serviceTabList.reduce((finaltabList: SelectionGroupOption[], tab) => {
        if (!isNullTabContent(tab.value as ServiceTabName)) {
          finaltabList.push(tab);
        }
        return finaltabList;
      }, []),
    [isNullTabContent, serviceTabList]
  );

  // Отрисовываем группу услуг, если она непустая
  const renderServiceGroup = useCallback(
    (serviceData: ServiceGroupStructure) => {
      const neededServices = getFinalServicesData(serviceData.list, landingsList, serviceCategoriesList);
      if (neededServices.length !== 0) {
        return (
          <ServicePanel
            name={serviceData.engName}
            title={t(serviceData.name)}
            renderIcon={() => (
              <img
                className={catalogGroupIcon}
                src={serviceData.iconSrc}
                alt={t(serviceData.name) || ''}
                width="48"
                height="48"
              />
            )}>
            {neededServices.map(service => (
              <LinkWithGAClickButton
                className={catalogItem}
                elementBlock="serviceList"
                elementName={service.serviceName}
                elementText={serviceData.name}
                rel="noreferrer"
                href={service.serviceUrl}
                title={service.serviceName}>
                <Typography className={catalogItemName} variant="m" size="body_long">
                  {service.serviceName.replace('и ', 'и\u00A0')}
                </Typography>
              </LinkWithGAClickButton>
            ))}
          </ServicePanel>
        );
      }
      return null;
    },
    [landingsList, serviceCategoriesList, t]
  );

  const handleServiceTabChange = (_: React.MouseEvent, value: SelectionGroupValue) => {
    setActiveServiceTab(value as ServiceTabName);

    const activeOption: SelectionGroupOption | undefined = serviceTabList.find(
      (option: SelectionGroupOption) => option.value === value
    );
    if (activeOption) {
      analyticsUtils.clickButtonEvent('serviceListFilter', activeOption.text, 'Категория услуг');
    }
  };

  return (
    <Section>
      <SelectionGroup
        className={catalogTabs}
        active={activeServiceTab}
        onChange={handleServiceTabChange}
        optionList={actualTabs}
        variant="outline"
        rounded
      />
      <ServiceAccordion>
        {SERVICES_CONTENT_DATA[activeServiceTab].map(serviceData => renderServiceGroup(serviceData))}
      </ServiceAccordion>
    </Section>
  );
};
